import React from 'react';

interface BasketballBackgroundProps {
  isDarkMode?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

const BasketballBackground: React.FC<BasketballBackgroundProps> = ({ 
  isDarkMode = false, 
  className = "",
  style = {}
}) => {
  // Color schemes for light and dark modes
  const colors = isDarkMode ? {
    primary: '#001b27',
    secondary: '#003c50', 
    accent: '#80ced7',
    light: '#9ad1d4',
    lightest: '#ccdbdc'
  } : {
    primary: '#003249',
    secondary: '#007ea7',
    accent: '#80ced7', 
    light: '#9ad1d4',
    lightest: '#ccdbdc'
  };

  const opacity = isDarkMode ? 0.8 : 0.6;
  const elementOpacity = isDarkMode ? 0.4 : 0.3;

  return (
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      viewBox="0 0 1920 1080" 
      preserveAspectRatio="xMidYMid slice"
      className={className}
      style={style}
    >
      <defs>
        <linearGradient id="mainGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" style={{stopColor: colors.primary, stopOpacity: 1}} />
          <stop offset="30%" style={{stopColor: colors.secondary, stopOpacity: 1}} />
          <stop offset="70%" style={{stopColor: colors.accent, stopOpacity: 1}} />
          <stop offset="100%" style={{stopColor: colors.light, stopOpacity: 1}} />
        </linearGradient>
        
        <radialGradient id="ballGradient" cx="30%" cy="30%">
          <stop offset="0%" style={{stopColor: colors.lightest, stopOpacity: opacity}} />
          <stop offset="70%" style={{stopColor: colors.light, stopOpacity: opacity * 0.8}} />
          <stop offset="100%" style={{stopColor: colors.secondary, stopOpacity: opacity * 0.6}} />
        </radialGradient>
        
        <filter id="glow">
          <feGaussianBlur stdDeviation="4" result="coloredBlur"/>
          <feMerge> 
            <feMergeNode in="coloredBlur"/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>
      </defs>
      
      {/* Base gradient background */}
      <rect width="100%" height="100%" fill="url(#mainGradient)"/>
      
      {/* Abstract court lines */}
      <g opacity={elementOpacity + 0.1} stroke={colors.lightest} strokeWidth="3" fill="none">
        {/* Center court circle */}
        <circle cx="960" cy="540" r="120" />
        <circle cx="960" cy="540" r="60" />
        
        {/* Side court arcs */}
        <path d="M 200 200 Q 400 350 200 500" />
        <path d="M 1720 200 Q 1520 350 1720 500" />
        <path d="M 200 580 Q 400 730 200 880" />
        <path d="M 1720 580 Q 1520 730 1720 880" />
      </g>
      
      {/* Improved basketball shapes with more authentic curves */}
      <g opacity={opacity}>
        {/* Large basketball - top left with authentic curves */}
        <circle cx="300" cy="200" r="80" fill="none" stroke={colors.accent} strokeWidth="2" />
        {/* Authentic basketball seam curves */}
        <path d="M 220 200 Q 260 140 300 120 Q 340 140 380 200" stroke={colors.accent} strokeWidth="2" fill="none" />
        <path d="M 220 200 Q 260 260 300 280 Q 340 260 380 200" stroke={colors.accent} strokeWidth="2" fill="none" />
        <path d="M 380 200 Q 340 140 300 120 Q 260 140 220 200" stroke={colors.accent} strokeWidth="2" fill="none" />
        <path d="M 380 200 Q 340 260 300 280 Q 260 260 220 200" stroke={colors.accent} strokeWidth="2" fill="none" />
        
        {/* Medium basketball - top right */}
        <circle cx="1500" cy="250" r="60" fill="url(#ballGradient)" opacity="0.7" />
        <path d="M 1440 250 Q 1470 210 1500 200 Q 1530 210 1560 250" stroke={colors.primary} strokeWidth="1.5" fill="none" />
        <path d="M 1440 250 Q 1470 290 1500 300 Q 1530 290 1560 250" stroke={colors.primary} strokeWidth="1.5" fill="none" />
        <path d="M 1560 250 Q 1530 210 1500 200 Q 1470 210 1440 250" stroke={colors.primary} strokeWidth="1.5" fill="none" />
        <path d="M 1560 250 Q 1530 290 1500 300 Q 1470 290 1440 250" stroke={colors.primary} strokeWidth="1.5" fill="none" />
        
        {/* Small basketball - bottom left */}
        <circle cx="400" cy="850" r="40" fill="url(#ballGradient)" opacity="0.8" />
        <path d="M 360 850 Q 380 820 400 815 Q 420 820 440 850" stroke={colors.primary} strokeWidth="1" fill="none" />
        <path d="M 360 850 Q 380 880 400 885 Q 420 880 440 850" stroke={colors.primary} strokeWidth="1" fill="none" />
        <path d="M 440 850 Q 420 820 400 815 Q 380 820 360 850" stroke={colors.primary} strokeWidth="1" fill="none" />
        <path d="M 440 850 Q 420 880 400 885 Q 380 880 360 850" stroke={colors.primary} strokeWidth="1" fill="none" />
      </g>
      
      {/* Floating geometric shapes */}
      <g opacity={elementOpacity}>
        <polygon points="100,100 150,50 200,100 175,150" fill={colors.light} />
        <polygon points="1600,800 1650,750 1700,800 1675,850" fill={colors.accent} />
        <polygon points="800,150 850,100 900,150 875,200" fill={colors.lightest} />
        
        {/* Curved abstract elements */}
        <path d="M 0 300 Q 200 250 400 300 Q 600 350 800 300" stroke={colors.light} strokeWidth="8" fill="none" opacity="0.5" />
        <path d="M 1120 700 Q 1320 650 1520 700 Q 1720 750 1920 700" stroke={colors.accent} strokeWidth="8" fill="none" opacity="0.5" />
      </g>
      
      {/* Subtle pattern overlay */}
      <g opacity={elementOpacity * 0.5}>
        <circle cx="150" cy="400" r="100" fill={colors.lightest} />
        <circle cx="1750" cy="600" r="120" fill={colors.light} />
        <circle cx="960" cy="100" r="80" fill={colors.accent} />
        <circle cx="500" cy="950" r="90" fill={colors.secondary} />
      </g>
      
      {/* Basketball hoop silhouettes */}
      <g opacity={elementOpacity + 0.1} fill={colors.lightest}>
        {/* Left hoop */}
        <rect x="50" y="520" width="80" height="40" rx="20" />
        <rect x="70" y="540" width="40" height="2" />
        <path d="M 70 542 L 75 560 L 85 565 L 95 565 L 105 560 L 110 542" stroke={colors.lightest} strokeWidth="1" fill="none" />
        
        {/* Right hoop */}
        <rect x="1790" y="520" width="80" height="40" rx="20" />
        <rect x="1810" y="540" width="40" height="2" />
        <path d="M 1810 542 L 1815 560 L 1825 565 L 1835 565 L 1845 560 L 1850 542" stroke={colors.lightest} strokeWidth="1" fill="none" />
      </g>
      
      {/* Subtle sparkle effects */}
      <g opacity={opacity} fill={colors.lightest}>
        <circle cx="600" cy="300" r="2" filter="url(#glow)" />
        <circle cx="1200" cy="200" r="1.5" filter="url(#glow)" />
        <circle cx="300" cy="700" r="1" filter="url(#glow)" />
        <circle cx="1600" cy="400" r="2.5" filter="url(#glow)" />
        <circle cx="800" cy="800" r="1.5" filter="url(#glow)" />
      </g>
    </svg>
  );
};

export default BasketballBackground;
